/**
 * browserVoice.js — v3.21-1754
 * =============================
 * Module-scope singleton shared by:
 *   • useBrowserHeadsetDevice   (owner — creates/destroys the Device)
 *   • useOutboundCall            (consumer — calls placeBrowserCall() when
 *                                 the operator's call mode is BROWSER_HEADSET)
 *
 * Contract:
 *   • Exactly ONE canonical Twilio Voice Device per authenticated browser
 *     session; the hook registers it via setActiveDevice() and clears it
 *     via clearActiveDevice() on disable/logout.
 *   • Consumers NEVER instantiate Devices directly — they only read
 *     getActiveDevice() / placeBrowserCall().
 *   • Includes a single, module-scope `navigator.mediaDevices.devicechange`
 *     listener attached only while a Device is active, so headset
 *     unplug/reconnect is detected without opening Audio Settings.
 */

let _device = null;                // the canonical Twilio Voice Device
let _onDeviceChange = null;        // { callback } → invoked on devicechange
let _devicechangeAttached = false;

function _attachDeviceChangeListener() {
  if (_devicechangeAttached) return;
  if (!navigator?.mediaDevices?.addEventListener) return;
  navigator.mediaDevices.addEventListener('devicechange', _fireDeviceChange);
  _devicechangeAttached = true;
}
function _detachDeviceChangeListener() {
  if (!_devicechangeAttached) return;
  try {
    navigator.mediaDevices.removeEventListener('devicechange', _fireDeviceChange);
  } catch (_) {}
  _devicechangeAttached = false;
}
function _fireDeviceChange() {
  if (typeof _onDeviceChange === 'function') {
    try { _onDeviceChange(); } catch (_) {}
  }
}

export function setActiveDevice(device, { onDeviceChange } = {}) {
  _device = device;
  _onDeviceChange = onDeviceChange || null;
  _attachDeviceChangeListener();
}

export function clearActiveDevice() {
  _device = null;
  _onDeviceChange = null;
  _detachDeviceChangeListener();
}

export function getActiveDevice() {
  return _device;
}

/**
 * placeBrowserCall — invoked by useOutboundCall when call mode is
 * BROWSER_HEADSET.  Returns the Twilio Call object on success, or
 * throws.  All required Hub metadata is passed as TwiML App parameters
 * so the existing backend TwiML/status-callback pipeline records the
 * call in the same activity/history/disposition surfaces.
 */
export async function placeBrowserCall({
  phone,
  clientId = null,
  dealId = null,
  clientName = null,
  callAs = 'eqf_ptc',
  context = null,
  userId = null,
  companyId = null,
} = {}) {
  if (!_device) {
    throw new Error('Browser Headset is not active on this workstation.');
  }
  const params = {
    To:         phone,
    CallAs:     callAs,
    ClientId:   clientId   || '',
    DealId:     dealId     || '',
    ClientName: clientName || '',
    UserId:     userId     || '',
    CompanyId:  companyId  || '',
    Source:     context    || 'hub_browser_headset',
  };
  // Twilio Voice SDK: Device.connect(...) returns a Promise<Call> in v2.x
  const call = await _device.connect({ params });
  return call;
}
